/**
 * CustomMenu — dropdown menu for user-created playlists.
 *
 * Renders an ellipsis trigger that opens a menu with edit / share / copy-link /
 * delete actions. Callers may pass `allowedActions` to restrict the visible
 * actions (e.g. an empty playlist exposes only edit + delete). See
 * `SystemMenu.tsx` for system playlists.
 *
 * Explicit variant rather than threading an `isSystemPlaylist` boolean
 * into a single component. Reuses the `profilePlaylistMenuOptions`
 * static-text slice and SCSS partial.
 *
 * **Static-text wiring**: this component does **not** mount its own
 * `connectStaticText` HOC + `<StaticTextProvider>`. The single, lifted
 * `<PlaylistMenuStaticTextScope>` in `ProfileMyPlaylists` provides the
 * `profilePlaylistMenuOptions` slice once for every menu instance — see
 * that file for the rationale.
 */

import React, { useCallback, useMemo } from 'react'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import useDisclosure from '../../../../../services/community/shared/useDisclosure'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import Edit from '../../../../Icon/icons/Edit/index'
import ShareAndroid from '../../../../Icon/icons/ShareAndroid/index'
import Link from '../../../../Icon/icons/Link/index'
import Delete from '../../../../Icon/icons/Delete/index'
import EllipsisHorizontal from '../../../../Icon/icons/EllipsisHorizontal/index'

export type PlaylistAction = 'edit' | 'share' | 'copyLink' | 'delete'

interface MenuItem {
  key: string
  IconComponent: React.ComponentType
  action: PlaylistAction
  label: string
}

const MENU_GROUPS: MenuItem[][] = [
  [
    {
      key: 'editPlaylist', IconComponent: Edit, action: 'edit', label: 'Edit Playlist',
    },
    {
      key: 'sharePlaylist', IconComponent: ShareAndroid, action: 'share', label: 'Share Playlist',
    },
  ],
  [
    {
      key: 'copyLink', IconComponent: Link, action: 'copyLink', label: 'Copy Link',
    },
  ],
  [
    {
      key: 'deletePlaylist', IconComponent: Delete, action: 'delete', label: 'Delete Playlist',
    },
  ],
]

export interface CustomMenuProps {
  playlistId: string
  playlistType?: string
  playlistName?: string
  /**
   * Optional allowlist of actions to render. When provided, `MENU_GROUPS` are
   * filtered to only include actions in this list. Use this to render a
   * focused subset of the menu — e.g. `['edit', 'delete']` for an empty
   * playlist that does not expose share / copyLink.
   */
  allowedActions?: PlaylistAction[]
  onAction?: ((
    playlistId: string,
    action: PlaylistAction,
    playlistType?: string,
    playlistName?: string,
  ) => void) | null
}

interface MenuItemRowProps {
  itemKey: string
  fallbackLabel: string
  IconComponent: React.ComponentType
  isDelete: boolean
  onClick: () => void
}

function MenuItemRow ({
  itemKey, fallbackLabel, IconComponent, isDelete, onClick,
}: MenuItemRowProps) {
  const resolved = useTranslatedText({ keys: [itemKey] })
  const label = resolved && resolved !== itemKey ? resolved : fallbackLabel
  return (
    <Button
      type={BUTTON_TYPES.LINK}
      role="menuitem"
      className={`profile-playlist-menu-options__item${isDelete ? ' profile-playlist-menu-options__item--delete' : ''}`}
      onClick={onClick}
    >
      <IconComponent />
      <span className="profile-playlist-menu-options__item-label">
        {label}
      </span>
    </Button>
  )
}

function CustomMenu ({
  playlistId,
  playlistType,
  playlistName,
  allowedActions,
  onAction = null,
}: CustomMenuProps) {
  const {
    isOpen, ref: dropdownRef, close, toggle,
  } = useDisclosure()
  const moreOptionsLabel = useTranslatedText({ keys: ['moreOptions'] })

  const menuGroups = useMemo(() => {
    if (!allowedActions) return MENU_GROUPS
    const allowed = new Set(allowedActions)
    return MENU_GROUPS
      .map((group) => group.filter((item) => allowed.has(item.action)))
      .filter((group) => group.length > 0)
  }, [allowedActions])

  const handleItemClick = useCallback((action: PlaylistAction) => {
    if (onAction) onAction(playlistId, action, playlistType, playlistName)
    close()
  }, [onAction, playlistId, playlistType, playlistName, close])

  return (
    <div className="profile-playlist-menu-options" ref={dropdownRef}>
      <Button
        type={BUTTON_TYPES.LINK}
        className="profile-playlist-menu-options__trigger"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={moreOptionsLabel}
      >
        <EllipsisHorizontal />
      </Button>
      {isOpen ? (
        <div className="profile-playlist-menu-options__menu" role="menu">
          {menuGroups.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && (
                <div className="profile-playlist-menu-options__divider" />
              )}
              {group.map((item) => (
                <MenuItemRow
                  key={item.key}
                  itemKey={item.key}
                  fallbackLabel={item.label}
                  IconComponent={item.IconComponent}
                  isDelete={item.action === 'delete'}
                  onClick={() => handleItemClick(item.action)}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default React.memo(CustomMenu)
